import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { Button } from '@/components/ui/button';
import { runStepAction } from './stepEvents';
import { useSetupAction } from '@/hooks/useSetup';
import { cn } from '@/lib/utils';
import type { SetupState, SetupStepId } from '@/types';
import { doneCount, focusTourTarget, type StepConfig } from './steps';

/**
 * The guide below 1024px: one bottom sheet per step with the dots, the action,
 * Skip and Hide, plus a "Setup n/7" pill fixed at the top right that opens the
 * sheet again once it has been tucked away.
 */
export function SetupSheet({
  state,
  steps,
  current,
  onPick
}: {
  state: SetupState;
  steps: StepConfig[];
  current: StepConfig | undefined;
  onPick: (id: SetupStepId | null) => void;
}) {
  const action = useSetupAction();
  const [open, setOpen] = useState(true);
  const { pathname } = useLocation();
  const done = doneCount(steps, state);
  const total = steps.length;
  const index = current ? steps.indexOf(current) : -1;

  function skip() {
    if (!current) return;
    action.mutate({ action: 'skipStep', step: current.id });
    const next = steps.slice(index + 1).find((s) => !s.isDone(state));
    onPick(next?.id ?? null);
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="tnum fixed top-3 right-3 z-40 rounded-full border border-[var(--line)] bg-[var(--surface)] px-3 py-1 text-xs font-medium shadow-sm lg:hidden"
      >
        Setup {done}/{total}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Setup"
          className="fixed inset-x-0 bottom-0 z-40 rounded-t-2xl border-t border-[var(--line)] bg-[var(--surface)] px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-2xl lg:hidden"
        >
          <div className="flex items-center justify-between">
            <ol className="flex items-center gap-1.5" aria-label={`Step ${index + 1} of ${total}`}>
              {steps.map((step) => {
                const isDone = step.isDone(state);
                const isCurrent = current?.id === step.id;
                return (
                  <li key={step.id}>
                    <button
                      type="button"
                      aria-label={step.title}
                      aria-current={isCurrent ? 'step' : undefined}
                      onClick={() => onPick(step.id)}
                      className={cn(
                        'block size-2 rounded-full',
                        isCurrent
                          ? 'bg-[var(--accent)] ring-2 ring-[var(--accent-soft)]'
                          : isDone
                            ? 'bg-[var(--accent)]/50'
                            : 'bg-[var(--line-strong)]'
                      )}
                    />
                  </li>
                );
              })}
            </ol>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="rounded-full p-1 text-muted hover:bg-[var(--surface-hover)]"
            >
              <ChevronDown className="size-4" />
            </button>
          </div>

          {current ? (
            <>
              <p className="mt-3 text-base font-semibold tracking-tight">{current.title}</p>
              <p className="mt-1 text-sm text-muted">{current.body}</p>
              <div className="mt-4 flex items-center gap-2">
                {current.cta.to && pathname === current.route ? (
                  <Button variant="primary" onClick={() => runStepAction(current.id) || focusTourTarget(current.target)}>
                    {current.cta.here ?? current.cta.label}
                  </Button>
                ) : (
                  <Button variant="primary" asChild>
                    <Link to={current.cta.to ?? current.route}>
                      {current.cta.to ? current.cta.label : 'Show me'}
                    </Link>
                  </Button>
                )}
                {!current.isDone(state) ? (
                  <Button variant="ghost" onClick={skip} disabled={action.isPending}>
                    Skip
                  </Button>
                ) : null}
                <Button
                  variant="ghost"
                  className="ml-auto"
                  onClick={() => action.mutate('hide')}
                  disabled={action.isPending}
                >
                  Hide setup
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="mt-3 text-base font-semibold tracking-tight">All set</p>
              <p className="mt-1 text-sm text-muted">Every step is done. Hide this when you like.</p>
              <div className="mt-4 flex items-center">
                <Button
                  variant="ghost"
                  className="ml-auto"
                  onClick={() => action.mutate('hide')}
                  disabled={action.isPending}
                >
                  Hide setup
                </Button>
              </div>
            </>
          )}
        </div>
      ) : null}
    </>
  );
}
